import React, { useState, useRef, useEffect } from "react";

/**
 * BlurImage — progressive image loader.
 *
 * Renders a low-opacity placeholder while the real image downloads, then
 * cross-fades to sharp. Uses the native `loading="lazy"` hint so images
 * outside the viewport are deferred. Falls back gracefully if the image
 * fails (keeps the placeholder gradient rather than a broken-icon).
 *
 * Design decision: no external `blurhash` dependency — a single dark-gold
 * gradient (matching the brand accent) reads as intentional cinematic
 * shimmer instead of a random smudge.
 */
export default function BlurImage({ src, alt, className = "", style, ...rest }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const imgRef = useRef(null);

  // If the image is already cached by the browser, `onLoad` may fire before
  // React attaches the handler — check `.complete` on mount.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  if (!src || failed) {
    return (
      <div
        className={`relative overflow-hidden ${className}`}
        style={{
          background:
            "linear-gradient(135deg, #EDE8DF 0%, #E0D9CC 60%, #D2C9B8 100%)",
          ...style,
        }}
        {...rest}
      />
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`} style={style} {...rest}>
      {/* Placeholder — visible until the real image is loaded. */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 transition-opacity duration-500 ${loaded ? "opacity-0" : "opacity-100"}`}
        style={{
          background:
            "linear-gradient(135deg, #EDE8DF 0%, #E0D9CC 60%, #D2C9B8 100%)",
        }}
      />
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
          loaded ? "opacity-100 blur-0 scale-100" : "opacity-0 blur-md scale-105"
        }`}
      />
    </div>
  );
}
